import {
  useState,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from 'react';
import { ChevronDown, ChevronRight, ExternalLink, Info } from 'lucide-react';
import type { TrustedMcpServer } from '@kirin/api-client/contracts/trusted-mcps';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useTrustedMcpServers } from '@/hooks/api/useTrustedMcpServers';
import { Button } from '@/components/ui/button';

/**
 * Columns describe what we project from each {@link TrustedMcpServer}.
 * Kept close to the editable MCP table's column shape so the surrounding
 * chrome (header row + grid template + cell paddings) can be reused
 * verbatim, but the IDs map directly to fields we actually have on
 * trusted entries (`vendor`, `rule.publisher`, `reason`).
 */
type TrustedColumn = {
  id: 'vendor' | 'publisher' | 'reason';
  headerLabel: string;
  headerTooltip?: string;
};

const COLUMNS: TrustedColumn[] = [
  { id: 'vendor', headerLabel: 'Vendor' },
  {
    id: 'publisher',
    headerLabel: 'Publisher',
    headerTooltip:
      'Publisher pattern Kirin matches against to identify this trusted vendor (supports wildcards).',
  },
  { id: 'reason', headerLabel: 'Reason' },
];

/**
 * Grid template mirrors the Blocked/Allowed editor exactly: N equal
 * data columns + a trailing slot for the per-row action (here: the
 * external-evidence link).
 */
const ROW_GRID_STYLE: CSSProperties = {
  gridTemplateColumns: `repeat(${COLUMNS.length}, minmax(0, 1fr)) minmax(4.5rem, auto)`,
};

function cellTextForColumn(
  server: TrustedMcpServer,
  column: TrustedColumn
): string {
  switch (column.id) {
    case 'vendor':
      return server.vendor;
    case 'publisher':
      return server.rule.publisher ?? '';
    case 'reason':
      return server.reason;
  }
}

/**
 * Full-width status line rendered inside the table body region. Reuses
 * the editor's dashed-border empty-state styling so loading / error /
 * empty all feel native to the surrounding table.
 */
function TableBodyStatus({
  tone = 'muted',
  role,
  ariaLive,
  children,
}: {
  tone?: 'muted' | 'destructive';
  role?: 'status' | 'alert';
  ariaLive?: 'polite' | 'off';
  children: ReactNode;
}): ReactElement {
  return (
    <div
      role={role}
      aria-live={ariaLive}
      className={cn(
        'my-3 rounded-md border border-dashed border-border/60 bg-card/40 px-3 py-2 text-center text-xs',
        tone === 'destructive' ? 'text-destructive' : 'text-muted-foreground'
      )}
    >
      {children}
    </div>
  );
}

/**
 * Read-only collapsible panel that renders the Kirin-provided trusted
 * list of MCPs (KIR-2376).
 *
 * Placement: mounted as the first child of the MCP allow/block form,
 * above the Blocked/Allowed editor, in collapsed state by default.
 * Rationale (product decision, May 2026): users opening the page see
 * the baseline first ("here is what Kirin already trusts; your rules
 * are below"), which neutralizes "did I add these?" / "do I need to
 * allow GitHub?" support questions. We deliberately accept asymmetry
 * with other allow/block policy pages — Trusted is a new concept that
 * only exists for MCPs, so some divergence is expected.
 *
 * Visual contract: when expanded, the inner table mirrors the Blocked
 * MCPs editor box (border, grid template, header style, empty-state
 * chrome) so the three lists read as a single precedence story —
 * Trusted → Blocked → Allowed. The trailing "actions" slot is reused
 * for an external link to the vendor's evidence URL; there is no
 * add / edit / remove affordance.
 *
 * The trigger is rendered eagerly so users always see that a trusted
 * list exists. Data fetching is deferred until the user opens the
 * panel for the first time — only then does `useTrustedMcpServers`
 * actually call `GET /api/system/trusted-mcps`. Loading, error and
 * empty states are surfaced **inside** the table body, not by hiding
 * the table. A small hint below the collapsed trigger nudges users to
 * expand the list and points them at the Block list for overrides;
 * the precedence note inside the expanded content gives the full
 * Trusted/Block/Allow ordering.
 */
export function TrustedMcpServersPanel(): ReactElement {
  const [open, setOpen] = useState(false);
  // Latch: once the user opens the panel we never disable the query
  // again, so collapsing-then-expanding does not retrigger a fetch
  // (React Query keeps the cached value while staleTime is fresh).
  // On error the latch is left in place; the user retries explicitly
  // via the in-panel "Retry" button below, which calls `refetch()`.
  const [hasOpened, setHasOpened] = useState(false);
  const { data, isLoading, isError, isFetching, refetch } =
    useTrustedMcpServers(hasOpened);

  const handleOpenChange = (next: boolean): void => {
    setOpen(next);
    if (next) {
      setHasOpened(true);
    }
  };

  const servers: TrustedMcpServer[] = data?.servers ?? [];
  const hasData = data !== undefined && !isError;
  const isLoadingFirstTime = hasOpened && (isLoading || isFetching) && !hasData;

  return (
    <Collapsible
      open={open}
      onOpenChange={handleOpenChange}
      className="rounded-lg border bg-card dark:bg-surface-elevated dark:border-surface-elevated-border"
      data-testid="trusted-mcp-servers-panel"
    >
      <CollapsibleTrigger
        className={cn(
          'flex w-full items-center justify-between gap-2 px-4 pt-4 text-left',
          // Tighten the bottom padding when the collapsed-state hint sits
          // directly below us, so the hint reads as part of the same
          // header block rather than floating in its own gap.
          open ? 'pb-4' : 'pb-2',
          'hover:bg-muted/50 rounded-lg transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
        )}
        aria-label={
          open ? 'Collapse Trusted MCP Servers' : 'Expand Trusted MCP Servers'
        }
      >
        <div className="flex items-center gap-2 min-w-0">
          {open ? (
            <ChevronDown className="size-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="size-4 text-muted-foreground" />
          )}
          <h3 className="text-sm font-semibold">Trusted MCP Servers</h3>
          {hasData && servers.length > 0 ? (
            <Badge
              variant="secondary"
              className="tabular-nums h-5 px-1.5 text-xs font-normal"
              aria-label={`${servers.length} trusted MCP servers`}
            >
              {servers.length}
            </Badge>
          ) : null}
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className="text-muted-foreground cursor-default"
                onClick={e => e.stopPropagation()}
              >
                <Info className="size-4" />
              </span>
            </TooltipTrigger>
            <TooltipContent
              sideOffset={6}
              className="max-w-md whitespace-normal leading-snug antialiased"
            >
              Kirin-vetted MCP server vendors. Security scans are skipped for
              these by default. Add any entry to your Block list to override.
              Read-only.
            </TooltipContent>
          </Tooltip>
        </div>
      </CollapsibleTrigger>
      {!open ? (
        <p
          className="px-4 pb-4 text-xs text-muted-foreground"
          data-testid="trusted-mcp-servers-collapsed-hint"
        >
          Expand to review Kirin-vetted MCP server vendors. Use the Block list
          below to override any entry.
        </p>
      ) : null}
      <CollapsibleContent className="px-4 pb-4 space-y-3">
        <p className="text-xs text-muted-foreground">
          <span className="font-medium text-foreground">Note:</span> Trusted MCP
          servers are not flagged by detection policies. A Block list entry will
          override this list.
        </p>
        <div
          role="table"
          aria-label="Trusted MCP Servers"
          className="flex flex-col max-h-[min(22rem,45vh)] rounded-md border bg-muted/20 dark:bg-background/55 overflow-x-hidden overflow-y-hidden"
        >
          <div
            role="row"
            style={ROW_GRID_STYLE}
            className="grid shrink-0 gap-x-2 gap-y-1 items-end px-2 pt-2 pb-1 border-b bg-card dark:bg-surface-elevated-muted"
          >
            {COLUMNS.map(col => (
              <div
                key={col.id}
                role="columnheader"
                className="min-w-0 flex items-end gap-1 text-xs font-medium text-muted-foreground dark:text-foreground/85"
              >
                <span className="min-w-0 truncate">{col.headerLabel}</span>
                {col.headerTooltip ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className="shrink-0 text-muted-foreground hover:text-foreground rounded-sm"
                        aria-label={`${col.headerLabel} column help`}
                      >
                        <Info className="size-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent
                      sideOffset={6}
                      className="max-w-sm whitespace-normal leading-snug antialiased"
                    >
                      {col.headerTooltip}
                    </TooltipContent>
                  </Tooltip>
                ) : null}
              </div>
            ))}
            <div className="min-w-0" aria-hidden />
          </div>
          <div
            role="rowgroup"
            className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain px-2 py-1.5 space-y-1.5"
          >
            {isError ? (
              <TableBodyStatus tone="destructive" role="alert">
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <span>Could not load trusted MCP servers</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={() => {
                      void refetch();
                    }}
                    disabled={isFetching}
                    aria-label="Retry loading trusted MCP servers"
                  >
                    {isFetching ? 'Retrying…' : 'Retry'}
                  </Button>
                </div>
              </TableBodyStatus>
            ) : isLoadingFirstTime ? (
              <TableBodyStatus role="status" ariaLive="polite">
                Loading trusted MCP servers…
              </TableBodyStatus>
            ) : !hasData ? (
              // Panel never opened yet; keep table empty (status row would be
              // misleading since no request has fired).
              <TableBodyStatus>No entries loaded yet.</TableBodyStatus>
            ) : servers.length === 0 ? (
              <TableBodyStatus>
                No trusted MCP servers are configured for this release.
              </TableBodyStatus>
            ) : (
              servers.map(server => (
                <div
                  key={server.vendor}
                  role="row"
                  style={ROW_GRID_STYLE}
                  className="grid gap-x-2 gap-y-1 items-center w-full min-w-0 border-b border-border/40 dark:border-border/80 py-2 pl-0.5 pr-1"
                >
                  {COLUMNS.map(col => {
                    const text = cellTextForColumn(server, col);
                    return (
                      <div
                        key={col.id}
                        role="cell"
                        className={cn(
                          'flex min-w-0 items-center gap-1.5 text-sm',
                          col.id === 'vendor' && 'font-medium',
                          col.id === 'reason' && 'text-muted-foreground'
                        )}
                      >
                        <span
                          className="min-w-0 flex-1 truncate"
                          title={text.length > 0 ? text : undefined}
                        >
                          {text}
                        </span>
                      </div>
                    );
                  })}
                  <div
                    role="cell"
                    className="flex min-w-0 items-center justify-end pr-0.5"
                  >
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <a
                          href={server.evidenceUrl}
                          target="_blank"
                          rel="noreferrer noopener"
                          className={cn(
                            'inline-flex h-8 w-8 items-center justify-center rounded-md',
                            'text-muted-foreground hover:bg-muted/85 hover:text-foreground',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                          )}
                          aria-label={`Evidence for ${server.vendor}`}
                        >
                          <ExternalLink className="size-3.5" />
                        </a>
                      </TooltipTrigger>
                      <TooltipContent
                        sideOffset={6}
                        className="max-w-sm whitespace-normal leading-snug antialiased"
                      >
                        Open evidence source
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
